import { useCallback, useEffect, useState } from "react";
import type { Quote, QuoteStatus } from "@fprint/shared";
import { getQuote, type TokenGetter } from "./api";

export type QuotePollState =
  | { phase: "idle" }
  | { phase: "polling"; quote: Quote | null }
  | { phase: "ready"; quote: Quote }
  // Multistage terminal states that are NOT payable-ready: `estimated`
  // (sliced, needs submit-for-review), `in_review` / `needs_manual_quote`
  // (operator has it — we email the final quote) and `declined`. All are
  // successful poll outcomes; the caller branches on `quote.status`.
  | { phase: "settled"; quote: Quote }
  | { phase: "failed"; quote: Quote }
  // The slicer may legitimately take longer than our polling window; this is
  // NOT a terminal error — the caller should offer "keep waiting" (which
  // calls `keepWaiting()` to resume polling the same quote id) instead of
  // forcing a re-upload.
  | { phase: "timeout"; quote: Quote | null }
  | { phase: "error"; error: string };

const SETTLED_STATUSES: readonly QuoteStatus[] = [
  "estimated",
  "in_review",
  "needs_manual_quote",
  "declined",
];

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000;

/**
 * Polls `GET /api/quotes/{id}` every 2 s until the quote resolves to
 * `ready`, a settled multistage status (`estimated`, `in_review`,
 * `needs_manual_quote`, `declined`) or `failed`. Returns a state machine
 * the page can render
 * directly, plus `keepWaiting()` to resume polling after a soft timeout.
 * Stops on unmount (clearing any pending timer) and on terminal states.
 */
export function useQuotePolling(
  quoteId: string | null,
  getToken?: TokenGetter,
): QuotePollState & { keepWaiting: () => void } {
  const [state, setState] = useState<QuotePollState>({ phase: "idle" });
  // Bumping this restarts the polling effect (fresh 60 s window) without
  // re-creating the quote.
  const [pollRun, setPollRun] = useState(0);

  const keepWaiting = useCallback(() => setPollRun((n) => n + 1), []);

  useEffect(() => {
    if (!quoteId) {
      setState({ phase: "idle" });
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const start = Date.now();

    async function tick(): Promise<void> {
      try {
        const quote = await getQuote(quoteId!, getToken);
        if (cancelled) return;
        if (quote.status === "ready") {
          setState({ phase: "ready", quote });
          return;
        }
        if (SETTLED_STATUSES.includes(quote.status)) {
          setState({ phase: "settled", quote });
          return;
        }
        if (quote.status === "failed") {
          setState({ phase: "failed", quote });
          return;
        }
        if (Date.now() - start > POLL_TIMEOUT_MS) {
          // Soft timeout: the slicer may still be working server-side.
          setState({ phase: "timeout", quote });
          return;
        }
        setState({ phase: "polling", quote });
        timer = setTimeout(tick, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setState({
          phase: "error",
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }

    setState((prev) =>
      prev.phase === "timeout"
        ? { phase: "polling", quote: prev.quote }
        : { phase: "polling", quote: null },
    );
    void tick();
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [quoteId, getToken, pollRun]);

  return { ...state, keepWaiting };
}
